/**
 * Chat do Especialista Fee-Based — o agente das telas de Fee Fixo.
 *
 * Reusa o motor SSE do `aawz-intelligence-v2` (mesma classe, mesmo streaming,
 * mesmo tratamento de erro) apontando para um namespace de store próprio. Sem
 * duplicar o cliente: o que muda é o namespace, não mais a porta de entrada.
 *
 * ★ [spec-010 T030] Até aqui `endpoints.chat` era a rota DEDICADA
 * (`/fee-fixo/chat`, agente e origem fixados pelo endpoint). A spec-010
 * aposenta as rotas dedicadas (D6/ADR-30, US-7): esta classe fala com a MESMA
 * porta única `/chat` de qualquer outra superfície do motor, e `perguntar()`
 * manda `origem_modulo='fee-fixo'` no corpo para o Supervisor saber DE ONDE a
 * pergunta partiu — o roteamento em si (qual agente responde) não é mais
 * escolha do endpoint nem do front.
 */
import store from '@state/store'
import { APIChat } from '@modules/aawz-intelligence-v2/api/apiChat'
import { feeFixoIa as endpoints } from '@modules/fee-fixo/constants/URL'
import { nomeUtil } from '@modules/fee-fixo/constants/Text'
import { ORIGEM_MODULO_FEE_FIXO } from '@modules/fee-fixo/constants/OrigemDoModulo'
import {
  ACOES_DO_ITEM_DESFECHO,
  ACOES_DO_ITEM_MIGRACAO,
  OPERACAO_CRIAR_SIMULACAO,
  TOOL_CRIAR_SIMULACAO,
  OPERACAO_DESFECHO_MIGRACAO,
  OPERACAO_MIGRACAO_EM_LOTE,
  TOOL_DESFECHO_MIGRACAO,
  TOOL_MIGRACAO_EM_LOTE,
} from '@modules/fee-fixo/constants/escritas-ia'
import {
  MARCA_DO_RASCUNHO,
  textoDoRascunho,
} from '@modules/fee-fixo/utils/rascunho-simulacao'
import { APIFeeFixoIaDeposito } from '@modules/fee-fixo/api/apiFeeFixoIaDeposito'

export const FEE_FIXO_IA_STORE = 'feeFixoIa'

// Tool de LEITURA que devolve o filtro tipado da aba Clientes (spec-012). O
// nome é o mesmo do `TOOL_SCHEMA` no backend
// (`aawz_intelligence_v2/tools/tool_fee_fixo.py`): é por ele que o `tool_result`
// é reconhecido no stream.
export const TOOL_FILTRO_CLIENTES = 'sugerir_filtro_clientes'

// Tool de LEITURA do estado de um cliente / do panorama da onda (spec-002 e
// spec-006). Nome ESPELHADO do `TOOL_SCHEMA` do backend
// (`aawz_intelligence_v2/tools/tool_fee_fixo.py:686`) — é por ele que o
// `tool_result` é reconhecido no stream, e trocá-lo de um lado só não quebra
// nada visivelmente: o evento chega sem dono, nenhum card abre, e a conversa
// segue como se a leitura nunca tivesse acontecido. O unit trava o valor.
export const TOOL_ESTADO_CLIENTE = 'estado_cliente_fee_fixo'

// Tool de LEITURA da priorização da carteira (spec-030). Nome ESPELHADO do
// `TOOL_SCHEMA` do backend (`tool_fee_fixo.py:568`). Ela entrou no roteador em
// 24/08/2026 ([G-F3], review de frontend): é a resposta em que a IA nomeia
// VÁRIOS clientes — o momento em que o consultor decide por quem começar — e
// era justamente ela que não oferecia atalho nenhum. O exemplo canônico da
// §6.5 da spec-006 ("quais clientes devo focar hoje?") roteia para cá, não
// para a tool de estado.
export const TOOL_PRIORIZAR_CARTEIRA = 'priorizar_carteira_fee_fixo'

// Tool de LEITURA do rascunho da simulação (spec-014). Mesmo nome do
// `TOOL_SCHEMA` da `ToolAnalisarRascunhoSimulacao`.
export const TOOL_RASCUNHO_SIMULACAO = 'analisar_rascunho_simulacao'

// As DUAS tools de ESCRITA da persona: entrar no funil da migração (spec-001)
// e registrar o DESFECHO dela (spec-002). Two-step nas duas: `confirmar=false`
// devolve o preview e não grava nada; `confirmar=true` grava, com changelog, na
// mesma transação. Os dois passos chegam pelo MESMO evento `tool_result`, com o
// mesmo `tool` — o que os separa é o `requer_confirmacao` dentro do `value`.
//
// O vocabulário (nomes de tool, marcas de operação e ações do item) vive em
// `constants/escritas-ia.js`, porque o card também o lê. Reexportado aqui: quem
// fala com a conversa importa desta porta desde a spec-001, e uma segunda porta
// só criaria duas grafias do mesmo import.
export {
  TOOL_MIGRACAO_EM_LOTE,
  OPERACAO_MIGRACAO_EM_LOTE,
  TOOL_DESFECHO_MIGRACAO,
  OPERACAO_DESFECHO_MIGRACAO,
  TOOL_CRIAR_SIMULACAO,
  OPERACAO_CRIAR_SIMULACAO,
}

// O que o consultor "diz" ao confirmar, nas DUAS vozes que a conversa tem:
// a de cima é o protocolo que o modelo lê (é ela que faz a segunda chamada sair
// com `confirmar=true`, em vez de um preview novo); a de baixo é a bolha que
// aparece na tela. Despejar o protocolo na bolha transformaria a conversa em
// log de máquina — mesma separação de `message` × `displayMessage` que o
// rascunho da simulação já usa.
export const MSG_CONFIRMAR_MIGRACAO =
  'Confirmo a lista que você mostrou. Chame ' +
  '`adicionar_clientes_migracao_fee_fixo` com `confirmar=true` para os MESMOS ' +
  'códigos do preview — não mude a lista.'

export const MSG_CONFIRMAR_MIGRACAO_EXIBIDA =
  'Confirmo — pode adicionar esses clientes ao funil da migração.'

/**
 * A mesma confirmação, agora NOMEANDO o preview que o servidor emitiu.
 *
 * ★ G-11 (verify iteração 3). O commit confere o `preview_id` contra o
 * registro do servidor (`migracao_preview_registro.consumir`) e RECUSA sem
 * ele. Até aqui, o único portador do uuid entre as duas chamadas era a memória
 * do modelo: bastava ele não repetir o valor para o "ok" do consultor virar um
 * preview novo em vez da gravação. Com o id na mensagem, quem o transporta é o
 * protocolo — e o modelo passa a ter de onde copiar.
 *
 * Sem id (o servidor não conseguiu registrar), a frase volta a ser a de antes:
 * mandar `preview_id=` vazio só ensinaria o modelo a inventar um.
 *
 * @param {string=} previewId `previewId` da pendência, como o captador o
 *                            normalizou.
 * @returns {string} Mensagem de PROTOCOLO (a bolha do consultor é a
 *                   `MSG_CONFIRMAR_MIGRACAO_EXIBIDA`, sem uuid).
 */
export const montarMensagemDeConfirmacao = (previewId) => {
  const id = String(previewId || '').trim()
  if (!id) return MSG_CONFIRMAR_MIGRACAO
  return (
    'Confirmo a lista que você mostrou. Chame ' +
    '`adicionar_clientes_migracao_fee_fixo` com `confirmar=true` e ' +
    '`preview_id=' +
    id +
    '` para os MESMOS códigos do preview — não mude a lista.'
  )
}

// O outro lado da mesma pergunta, nas mesmas duas vozes (S-QA-3 do verify
// iteração 2). Cancelar o card limpava só a store, e o modelo — que não vê a
// store — reoferecia o preview que o consultor acabara de descartar. Continua
// sem gravar nada; o que muda é que a CONVERSA passa a saber que houve um não.
export const MSG_RECUSAR_MIGRACAO =
  'Descartei a lista que você mostrou. NÃO chame ' +
  '`adicionar_clientes_migracao_fee_fixo` de novo e não ofereça esses clientes ' +
  'outra vez enquanto eu não pedir explicitamente.'

export const MSG_RECUSAR_MIGRACAO_EXIBIDA = 'Não vou adicionar agora.'

/**
 * As mesmas duas vozes para a SEGUNDA escrita — o desfecho da migração.
 *
 * ★ O texto de `montarMensagemDeConfirmacaoDoDesfecho` é CONTRATO com o
 * servidor, não prosa: `protocolo_confirmacao_desfecho.PADRAO_DA_CONFIRMACAO`
 * (backend) casa este trecho ANTES do LLM e executa o commit pelo caminho da
 * própria tool. Uma vírgula a mais aqui e o servidor não intercepta nada — a
 * confirmação segue para o modelo, que é onde o QA3 da spec-001 mediu ZERO
 * gravação (o modelo lê um `preview_id` vindo do usuário, conclui que é
 * ilegítimo e re-emite preview). O padrão de lá exige um uuid, e difere do
 * irmão na ABERTURA e no NOME da ferramenta: é o que torna a colisão entre os
 * dois cards impossível.
 *
 * Sem id (o servidor não conseguiu registrar o preview), a frase sai sem o
 * parâmetro — mandar `preview_id=` vazio só ensinaria o modelo a inventar um.
 * O card, nesse estado, nem oferece o Confirmar.
 */
export const MSG_CONFIRMAR_DESFECHO =
  'Confirmo o desfecho que você mostrou. Chame ' +
  '`registrar_desfecho_migracao_fee_fixo` com `confirmar=true` para o MESMO ' +
  'cliente do preview — não mude o desfecho.'

export const MSG_CONFIRMAR_DESFECHO_EXIBIDA =
  'Confirmo — pode registrar esse desfecho.'

/**
 * @param {string=} previewId `previewId` da pendência, como o captador o
 *                            normalizou.
 * @returns {string} Mensagem de PROTOCOLO (a bolha do consultor é a
 *                   `MSG_CONFIRMAR_DESFECHO_EXIBIDA`, sem uuid).
 */
export const montarMensagemDeConfirmacaoDoDesfecho = (previewId) => {
  const id = String(previewId || '').trim()
  if (!id) return MSG_CONFIRMAR_DESFECHO
  return (
    'Confirmo o desfecho que você mostrou. Chame ' +
    '`registrar_desfecho_migracao_fee_fixo` com `confirmar=true` e ' +
    '`preview_id=' +
    id +
    '` para o MESMO cliente do preview — não mude o desfecho.'
  )
}

// A recusa do desfecho, nas mesmas duas vozes. Nada é gravado nem desfeito (o
// preview não criou linha nenhuma); o que ela faz é contar ao MODELO que houve
// um não — sem isso ele reoferece o desfecho que o consultor acabou de
// descartar, como aconteceu com a lista na spec-001 (S-QA-3).
export const MSG_RECUSAR_DESFECHO =
  'Descartei o desfecho que você mostrou. NÃO chame ' +
  '`registrar_desfecho_migracao_fee_fixo` de novo e não registre desfecho ' +
  'nenhum para esse cliente enquanto eu não pedir explicitamente.'

export const MSG_RECUSAR_DESFECHO_EXIBIDA = 'Não vou registrar agora.'

// Operação → as duas vozes de cada resposta do card. É esta tabela que permite
// ao card e à view servirem as DUAS escritas sem um `if` por método:
// acrescentar uma terceira operação é acrescentar uma linha — a mesma forma do
// `PADRAO_POR_OPERACAO` do servidor (`protocolo_confirmacao_migracao.py`), e
// pelo mesmo motivo (um `if` no meio do caminho de quem já funciona é onde a
// operação errada vaza).
// ── A voz da spec-007: salvar a simulação que a conversa montou ─────────────

/** Sem `preview_id` o commit recusa — e o card, nesse estado, esconde o botão. */
const MSG_CONFIRMAR_SIMULACAO =
  'Confirmo — pode salvar essa simulação no Fee Lab.'

/** A bolha do consultor: sem uuid, que para ele é ruído. */
const MSG_CONFIRMAR_SIMULACAO_EXIBIDA =
  'Confirmo — pode salvar essa simulação no Fee Lab.'

const MSG_RECUSAR_SIMULACAO =
  'Não salve essa simulação. Descarte o preview e siga sem gravar nada.'

const MSG_RECUSAR_SIMULACAO_EXIBIDA = 'Não salve essa simulação.'

/**
 * ★ TRANSCRITA do detector do servidor
 * (`core/protocolo_confirmacao_simulacao.py:65-71`), caractere a caractere — as
 * crases nos dois nomes, `MESMO` em caixa alta, o travessão `—` com espaços e o
 * ponto final. **Não derive**: é contrato entre dois repos, e o casamento tem
 * de falhar quando um dos lados mudar sozinho. O servidor tem controle negativo
 * provando que trocar só `MESMO` por `mesmo` não casa.
 *
 * O `preview_id` viaja no TEXTO porque a interceptação determinística o
 * reconhece **antes** do modelo. Não é preciosismo: o modelo já fabricou uma
 * confirmação (escreveu o preview em prosa, o consultor respondeu "Confirmo", e
 * a chamada seguinte veio com `confirmar=true`) e, na versão seguinte, recusou
 * um `preview_id` legítimo por achá-lo suspeito — dois previews, zero
 * gravações, tela indistinguível de sucesso.
 *
 * @param {string=} previewId `previewId` da pendência.
 * @returns {string} Mensagem de PROTOCOLO.
 */
export const montarMensagemDeConfirmacaoDaSimulacao = (previewId) => {
  const id = String(previewId || '').trim()
  if (!id) return MSG_CONFIRMAR_SIMULACAO
  return (
    'Confirmo a simulação que você mostrou. Chame ' +
    '`criar_simulacao_fee_fixo` com `confirmar=true` e `preview_id=' +
    id +
    '` para o MESMO cenário do preview — não mude os números.'
  )
}

const VOZES_POR_OPERACAO = {
  [OPERACAO_MIGRACAO_EM_LOTE]: {
    confirmar: montarMensagemDeConfirmacao,
    confirmarExibida: MSG_CONFIRMAR_MIGRACAO_EXIBIDA,
    recusar: MSG_RECUSAR_MIGRACAO,
    recusarExibida: MSG_RECUSAR_MIGRACAO_EXIBIDA,
  },
  [OPERACAO_DESFECHO_MIGRACAO]: {
    confirmar: montarMensagemDeConfirmacaoDoDesfecho,
    confirmarExibida: MSG_CONFIRMAR_DESFECHO_EXIBIDA,
    recusar: MSG_RECUSAR_DESFECHO,
    recusarExibida: MSG_RECUSAR_DESFECHO_EXIBIDA,
  },
  [OPERACAO_CRIAR_SIMULACAO]: {
    confirmar: montarMensagemDeConfirmacaoDaSimulacao,
    confirmarExibida: MSG_CONFIRMAR_SIMULACAO_EXIBIDA,
    recusar: MSG_RECUSAR_SIMULACAO,
    recusarExibida: MSG_RECUSAR_SIMULACAO_EXIBIDA,
  },
}

/**
 * As operações que o card GENÉRICO (`fee-fixo-guia-confirmacao`) desenha.
 *
 * A store é compartilhada com o painel lateral, que guarda no mesmo lugar o
 * preview de envio de e-mail: sem esta lista, o card ofereceria "confirmar"
 * para um e-mail.
 *
 * ★ Ela é DECLARADA, não derivada de `VOZES_POR_OPERACAO` — as duas listas
 * respondem perguntas diferentes e deixaram de coincidir em 24/08/2026. A
 * simulação (spec-007) tem protocolo de confirmação, e por isso está na tabela
 * de vozes; o que ela **não** tem é lugar no card genérico, porque desenha o
 * cenário nos componentes de verdade do Fee Lab
 * (`fee-fixo-guia-simulacao-card`). Derivar uma da outra faria a próxima
 * escrita com card próprio aparecer aqui sozinha — e o card genérico montaria
 * sobre ela com título vazio em cima do botão que autoriza a escrita.
 */
export const OPERACOES_COM_CARD = [
  OPERACAO_MIGRACAO_EM_LOTE,
  OPERACAO_DESFECHO_MIGRACAO,
]

/**
 * O que o consultor "diz" ao confirmar, nas duas vozes, para a operação certa.
 *
 * @param {string} operacao   Marca da operação, como o preview a trouxe.
 * @param {string=} previewId `previewId` da pendência.
 * @returns {?{message: string, exibir: string}} `null` para operação sem card —
 *   inventar protocolo para um preview que esta tela não sabe montar seria
 *   pedir uma escrita que ninguém revisou.
 */
export const mensagemDeConfirmacao = (operacao, previewId) => {
  const vozes = VOZES_POR_OPERACAO[operacao]
  if (!vozes) return null
  return {
    message: vozes.confirmar(previewId),
    exibir: vozes.confirmarExibida,
  }
}

/**
 * O outro lado da mesma pergunta — o "não", também nas duas vozes.
 *
 * @param {string} operacao Marca da operação, como o preview a trouxe.
 * @returns {?{message: string, exibir: string}}
 */
export const mensagemDeRecusa = (operacao) => {
  const vozes = VOZES_POR_OPERACAO[operacao]
  if (!vozes) return null
  return {
    message: vozes.recusar,
    exibir: vozes.recusarExibida,
  }
}

// O `value` vem do modelo pelo SSE: campo ausente ou de outro tipo não pode
// atravessar até o componente e virar `undefined.length` no meio de um computed.
const normalizarFiltroSugerido = (value) => ({
  valido: !!value.valido,
  filtros: Array.isArray(value.filtros) ? value.filtros : [],
  erros: Array.isArray(value.erros) ? value.erros : [],
})

// Só `{premissa, direcao, efeito}` atravessa, e nada mais.
//
// ★ É a fronteira de grounding do lado da tela. O backend monta `o_que_mudaria`
//   SEM número de propósito (`O_QUE_MUDARIA`, `tools_fee_fixo.py`) — quem
//   calcula é o engine. Deixar um campo extra do item entrar na store abriria a
//   porta para um `valor_sugerido` virar número exibido sem engine por trás,
//   que é justamente o que o gate de grounding do servidor impede do lado de lá.
const normalizarItemDoQueMudaria = (item) => ({
  premissa: String((item && item.premissa) || ''),
  direcao: String((item && item.direcao) || ''),
  efeito: String((item && item.efeito) || ''),
})

// Item do preview: só código, nome e ação atravessam. O `tool_result` vem do
// modelo, e um campo a mais aqui (um `auc`, um `id_cliente_app`) viraria dado
// exibido — ou pior, enviado de volta — sem ter passado pela peneira de tenant
// que o motor aplica do lado de lá.
//
// A lista de ações é PARÂMETRO, e não uma união das quatro: cada escrita tem as
// suas, e um `ja_no_funil` que atravessasse no card do desfecho acenderia ali o
// aviso "vou pular" — que não existe nesta operação (ela FECHA uma conversa,
// não pula nenhuma).
const normalizarItemDoPreview = (item, acoesPermitidas) => {
  const acao = String((item && item.acao) || '')
  return {
    codigo: String((item && item.codigo) || ''),
    // `nome_cliente` chega nulo quando o cadastro não tem nome — é o caso comum
    // na base importada, e quem resolve a exibição é o card.
    nome: String((item && item.nome) || ''),
    acao: acoesPermitidas.indexOf(acao) >= 0 ? acao : '',
  }
}

// Tool de ESCRITA → ações válidas do item dela. É esta tabela que faz o
// captador rotear: escrita nova é uma linha aqui, nunca um `if` a mais no
// caminho de quem já funciona — e é ela que garante que cada card só receba as
// ações da própria operação.
const ACOES_DO_ITEM_POR_TOOL = {
  [TOOL_MIGRACAO_EM_LOTE]: ACOES_DO_ITEM_MIGRACAO,
  [TOOL_DESFECHO_MIGRACAO]: ACOES_DO_ITEM_DESFECHO,
}

const normalizarPreviewDeEscrita = (value, acoesPermitidas) => ({
  operacao: String(value.operacao || ''),
  // O resumo é do motor (quantos entram, quantos são pulados). A tela não
  // reescreve nem recalcula — só mostra.
  resumo: String(value.resumo || ''),
  // ★ G-11 (verify iteração 3). O id que AUTORIZA o commit: nasce no servidor
  // (`migracao_preview_registro.registrar`) e é conferido lá na gravação. Até
  // aqui ele era descartado, e o uuid só existia na fala do modelo — se ele
  // não o repetisse na segunda chamada, o "Confirmar" do consultor virava um
  // preview novo em vez da escrita. Guardado, a view pode nomeá-lo na
  // mensagem de confirmação e deixar de depender da memória do modelo.
  //
  // Vazio (e não `null`) quando ausente: o card testa a presença, e um só
  // tipo falsy evita o `=== null` que erra com `undefined`.
  previewId: value.preview_id ? String(value.preview_id) : '',
  // Por que o servidor não registrou (Redis fora do ar, por exemplo). Vem
  // redigido para o MODELO — quem fala com o consultor é o card, com texto
  // próprio. Guardar mesmo assim é o que permite descobrir o motivo sem
  // reabrir o log do servidor.
  aviso: String(value.aviso || ''),
  itens: Array.isArray(value.itens)
    ? value.itens.map((item) => normalizarItemDoPreview(item, acoesPermitidas))
    : [],
})

const normalizarRascunhoAnalisado = (value) => ({
  temRascunho: !!value.tem_rascunho,
  oQueMudaria: Array.isArray(value.o_que_mudaria)
    ? value.o_que_mudaria.map(normalizarItemDoQueMudaria)
    : [],
  // Flags da leitura do cenário (cruza, economia negativa). São booleanos ou
  // `null` — nunca número, então passam como vieram depois do teste de tipo.
  leitura:
    value.leitura && typeof value.leitura === 'object' ? value.leitura : {},
})

/**
 * Um cliente citado, no shape que `abrirConversaDeCliente` consome.
 *
 * Sem `id_cliente_app` não há atalho: é ele que identifica o cadastro, e o
 * código sozinho é AMBÍGUO no escritório 411 (o mesmo `000124` existe em dois
 * cadastros, de profissionais distintos — ADR-005).
 */
const clienteCitado = (bruto) => {
  const idClienteApp = bruto && bruto.id_cliente_app
  if (!idClienteApp) return null
  return {
    idClienteApp,
    codigo: String((bruto && bruto.codigo) || ''),
    nome: String((bruto && bruto.nome) || ''),
  }
}

/**
 * Os clientes que a resposta IDENTIFICOU, para o cartão de atalho (spec-006).
 *
 * Duas formas do mesmo `value`, e três produtores. A leitura de UM cliente
 * devolve `cliente`; o panorama da onda e a PRIORIZAÇÃO
 * (`priorizacao_carteira.py:465`) devolvem `clientes[]`. Ler as duas formas
 * aqui é o que faz o cartão aparecer no "como está o 000123?", no pedido da
 * onda inteira e no "por quem começo?" — este último é o exemplo canônico da
 * §6.5 e o momento em que a IA nomeia mais gente.
 *
 * ⚠️ O `id_cliente_app` da priorização é `None` para cliente sem cadastro no
 * app (o backend não o inventa), e `clienteCitado` já descarta esse caso: sem
 * id não há atalho, porque o código sozinho é ambíguo.
 *
 * ★ AMBIGUIDADE NÃO VIRA ATALHO, e o que garante isso é a LISTA LIDA. Quando
 * mais de um cadastro casa com o pedido, o backend devolve `requer_escolha` com
 * `candidatos` — e nenhum `cliente`/`clientes`
 * (`estado_cliente_conversa.py:249-265`). Os candidatos TÊM `id_cliente_app`:
 * ler essa chave aqui faria o cartão escolher por quem tinha de escolher, que é
 * o consultor. Por isso ela fica de fora, e o unit trava o caso.
 *
 * ⚠️ Uma guarda `if (requer_escolha) return []` chegou a existir aqui e SAIU: a
 * mutação mostrou que ela era inalcançável (sem `cliente` no envelope, o
 * resultado já era `[]`), e o teste que a "cobria" passava com ela desligada.
 * Caminho que nenhuma mutação alcança é caminho que não faz nada.
 */
export const normalizarClientesCitados = (value) => {
  const bruto = value && typeof value === 'object' ? value : {}
  const lista = Array.isArray(bruto.clientes)
    ? bruto.clientes
    : [bruto.cliente]
  return lista.map(clienteCitado).filter(Boolean)
}

// Rótulo de cada tela para o bloco de contexto. Nome de rota → o que o
// consultor está olhando quando pergunta.
//
// O rótulo já vem com a preposição contraída ("na visão geral", "no Fee Lab")
// porque as telas têm gêneros diferentes: montar a frase com um "em" fixo
// produzia "O consultor está em a visão geral" em toda requisição.
const CONTEXTO_POR_ROTA = {
  // A landing entra no mapa como qualquer aba porque o drawer do Especialista
  // vive no shell (`fee-fixo-nav-bar.vue`) e acompanha TODAS as telas filhas:
  // quem pergunta daqui ainda não abriu dado nenhum, e o agente precisa saber
  // disso antes de responder como se houvesse carteira na frente do consultor.
  'fee-fixo.welcome':
    'na tela de boas-vindas do módulo — a apresentação do que o Fee Fixo faz e das abas disponíveis, ainda sem nenhum dado da carteira aberto',
  // Entrada em conversa do módulo (spec-001 do
  // project-onboarding-with-ai-fee-fixo). Quem pergunta daqui está na PRÓPRIA
  // conversa, não numa tela de dados: não há filtro aplicado nem cliente
  // aberto na frente dele, e o agente precisa saber disso antes de responder
  // como se houvesse. Entra aqui porque `apiFeeFixoIa.unit.js` exige que TODA
  // rota do módulo tenha rótulo — rota nova sem contexto reprova a suíte.
  'fee-fixo.guia':
    'na entrada do módulo — a conversa com o guia da migração, ao lado do cartão que resume o estado da carteira (base cadastrada, cenários já rodados e clientes no funil)',
  'fee-fixo.dashboard':
    'na visão geral da carteira em migração (KPIs do escritório/assessor)',
  'fee-fixo.clientes':
    'na lista de clientes da carteira, com AuC, tier e status de migração',
  'fee-fixo.simulacao':
    'no Fee Lab — a simulação de custo comissionado x fee-based',
  'fee-fixo.simulacao.cliente':
    'no Fee Lab — a simulação de custo comissionado x fee-based',
  'fee-fixo.pipeline':
    'no acompanhamento do funil de migração (oportunidades por etapa)',
  'fee-fixo.configuracoes':
    'na aba de Configurações, onde ficam as premissas do escritório que alimentam todo o módulo (take rate e cashback por classe, alocações, fee, giro e padrões de cálculo)',
  // `fee-fixo.roleplay` (o nome bare) virou rota-shell, só com `redirect` —
  // desde o redesign 07/08/2026 (7ª rodada, BaseSegmentedTabs + 2 páginas
  // reais) `$route.name` nunca resolve pra ele em produção, sempre pra uma
  // das duas filhas abaixo. Ver routes.js.
  'fee-fixo.roleplay.ensaio':
    'no ensaio da conversa de migração — treinando o discurso contra um cliente simulado que argumenta com os números do cenário salvo dele',
  'fee-fixo.roleplay.historico':
    'no histórico de ensaios anteriores',
}

// A abertura do bloco de contexto e a cola entre os blocos de uma pergunta.
// São constantes porque o histórico precisa DESFAZER o que aqui se monta: a
// bolha do consultor mostra a fala dele, não o prompt (ver `falaDoConsultor`).
export const MARCA_DO_CONTEXTO = '[Contexto da tela:'
export const SEPARADOR_DAS_PARTES = '\n\n'

// Os blocos que `perguntar` prepende à fala do consultor. Cada marca vem do
// arquivo que MONTA o bloco correspondente — nunca redigitada.
const MARCAS_DO_PROMPT = [MARCA_DO_CONTEXTO, MARCA_DO_RASCUNHO]

const marcaQueAbre = (texto) =>
  MARCAS_DO_PROMPT.find((marca) => texto.indexOf(marca) === 0) || ''

/**
 * Monta o bloco de contexto que acompanha a pergunta.
 *
 * Só descreve ONDE o consultor está e QUEM está em foco — os números continuam
 * vindo das tools do agente. Passar valor de tela aqui viraria número sem
 * fonte, exatamente o que a spec-003 proíbe.
 *
 * ★ **O id do cadastro serve de identificação quando o código ainda não foi
 * resolvido** (G-Q4/G-Q3, 23/08/2026): na entrada do guia a conversa de um
 * cliente pode abrir sabendo só o `id_cliente_app` — a lista de clientes não é
 * carregada ali e a esteira ainda está em voo. Sem nada no bloco, o modelo
 * respondia "preciso identificar o cliente, me passa o código" numa conversa
 * que É daquele cliente. As três tools da persona aceitam `id_cliente_app`
 * (`estado_cliente_fee_fixo`, entre outras), então o id é identificação
 * utilizável, não número solto — o código continua preferido quando existe.
 *
 * @param {Object=} opts
 * @param {string=} opts.routeName      Nome da rota atual.
 * @param {string=} opts.codigoCliente  Cliente em foco, quando houver.
 * @param {string=} opts.nomeCliente    Nome do cliente em foco, quando houver.
 * @param {number=} opts.idClienteApp   Id do cadastro — usado SÓ quando o
 *                                      código ainda não foi resolvido.
 * @returns {string} Bloco de contexto, ou string vazia se não houver o que dizer.
 */
export function montarContexto({
  routeName,
  codigoCliente,
  nomeCliente,
  idClienteApp,
} = {}) {
  const tela = CONTEXTO_POR_ROTA[routeName]
  const partes = []
  if (tela) partes.push(`O consultor está ${tela}.`)
  if (codigoCliente) {
    // Mesma regra do cabeçalho do painel: nome igual ao código não identifica
    // ninguém, e mandar "2092367 (2092367)" só gasta contexto do agente.
    const identificado = nomeUtil({ nome: nomeCliente, codigo: codigoCliente })
    const nome = identificado
      ? `${identificado} (${codigoCliente})`
      : codigoCliente
    partes.push(`Cliente em foco: ${nome}.`)
  } else if (idClienteApp) {
    // `id_cliente_app=<n>` é a forma que as tools aceitam — escrever o número
    // solto faria o modelo tratá-lo como código da corretora e abrir a esteira
    // de outro cliente.
    partes.push(`Cliente em foco: id_cliente_app=${idClienteApp}.`)
  }
  if (partes.length === 0) return ''
  return `${MARCA_DO_CONTEXTO} ${partes.join(' ')}]`
}

/**
 * O que o CONSULTOR escreveu — o inverso do que `perguntar` monta.
 *
 * ★ G-Q1 (QA, 23/08/2026). Ao vivo a bolha do consultor usa `displayMessage`,
 * que é estado local e não vai no corpo HTTP. Depois do F5 o histórico vem do
 * banco, e o que está gravado ali é o que o MODELO leu: o bloco de contexto (e,
 * na Simulação, o do rascunho) na frente da pergunta. A bolha exibia
 * `[Contexto da tela: O consultor está na entrada do módulo…` — prompt interno
 * na fala de quem não o escreveu.
 *
 * As marcas e o separador são as MESMAS constantes que montam a mensagem
 * (`MARCA_DO_CONTEXTO`, `MARCA_DO_RASCUNHO`, `SEPARADOR_DAS_PARTES`): redigitar
 * qualquer uma delas aqui faria o strip parar de casar EM SILÊNCIO no dia em
 * que o texto mudasse, e a bolha voltaria ao defeito sem nada ficar vermelho.
 *
 * **Falha segura:** o que não começa por uma marca conhecida — ou começa e não
 * traz o separador — volta INTEIRO. Cortar a fala do consultor é pior do que
 * deixar um bloco aparecer, e a fala dele pode ter o mesmo `\n\n` no meio.
 *
 * @param {*} texto `content` da mensagem, como veio do banco.
 * @returns {*} O texto sem os blocos do prompt, ou ele mesmo.
 */
export function falaDoConsultor(texto) {
  if (typeof texto !== 'string') return texto
  let atual = texto
  // `while`, e não um `if`: os dois blocos podem vir juntos na mesma mensagem
  // (contexto + rascunho, que é o caso do painel da Simulação).
  let marca = marcaQueAbre(atual)
  while (marca) {
    const corte = atual.indexOf(SEPARADOR_DAS_PARTES)
    if (corte === -1) return atual
    atual = atual.slice(corte + SEPARADOR_DAS_PARTES.length)
    marca = marcaQueAbre(atual)
  }
  return atual
}

class APIFeeFixoIa extends APIChat {
  constructor() {
    super({ storeNamespace: FEE_FIXO_IA_STORE, endpoints: endpoints })
    // O dono do depósito de `resultados` (POST em voo na instância dele).
    this._deposito = new APIFeeFixoIaDeposito({ storeNamespace: FEE_FIXO_IA_STORE })
  }

  /**
   * O erro do turno vai à TELA, e não só ao console ([G-A6]).
   *
   * ★ O caso que nomeou o gap: o backend RECUSA gravar numa conversa removida
   * (`ConversaRemovidaNaoRecebeMensagem`) e manda a frase — "Esta conversa foi
   * removida e não recebe mensagens novas. Comece uma conversa nova para
   * continuar." — pelo evento SSE `error`. Nenhum caminho a levava à tela: o
   * `_onEventError` do motor só faz `console.error`; o `reconnect` que ele
   * tentaria depende de `data.id`, que nesse evento vai `null`; e o
   * `_executeStream` devolve `this.responseData || {}` — vazio, porque
   * `responseData` só é preenchido pelo evento `end`, que nunca vem —, então o
   * `if (r.status !== 200) $showSnack(r)` da view não dispara. O consultor via
   * a própria bolha e mais nada.
   *
   * Sobrescrevemos aqui, e não no motor comum, porque quem RENDERIZA a frase é
   * a tela do Fee Fixo: publicar um slice novo no `aawz-intelligence-v2`
   * mudaria o contrato do chat do CRM sem ninguém do lado de lá ter pedido. A
   * invisibilidade do evento `error` é PRÉ-EXISTENTE e vale para toda falha do
   * stream nas duas superfícies — o que muda é que esta é a primeira rota que
   * DEPENDE dela para se explicar.
   *
   * ⚠️ Quando o erro vem com `id`, o motor RECONECTA e o stream continua: aí a
   * frase não entra, senão sobraria um erro na tela por cima da resposta que
   * chegou logo depois. Quem decide é o mesmo dado que o motor usa (`data.id`),
   * lido ANTES do `super` — que é quem levanta o `reconnectCalled`.
   *
   * @param {Object} data Payload do evento SSE `error` (`{id, message}`).
   */
  _onEventError(data) {
    const vaiReconectar = Boolean(data && data.id) && !this.reconnectCalled
    super._onEventError(data)
    if (vaiReconectar) return
    const mensagem = (data && data.message) || ''
    store.dispatch(
      FEE_FIXO_IA_STORE + '/setErroDoTurno',
      mensagem ||
        'Não foi possível responder agora. Tente de novo em instantes; se persistir, contate o suporte.'
    )
  }

  /**
   * A bolha de erro do thread NÃO entra aqui — esta superfície já tem a dela.
   *
   * O motor comum passou a gravar uma mensagem de assistente em estado de erro
   * quando o turno morre (G-2 da VERIFY it.2 da spec-001 `plataforma-por-chat`),
   * com a `message` do servidor e um "Tentar de novo". Nas telas do Fee Fixo
   * quem mostra a mesma frase é o bloco `[data-teste="turno-erro"]`, alimentado
   * por `feeFixoIa/erroDoTurno` ([G-A6]) — deixar as duas correrem juntas
   * imprimiria a MESMA explicação duas vezes na conversa.
   *
   * E o botão faria mal aqui: a recusa que motivou o [G-A6] é a da conversa
   * REMOVIDA, que é determinística. "Tentar de novo" a repetiria para sempre —
   * a própria frase do servidor manda começar uma conversa nova, e é o
   * "Nova conversa" da lateral que resolve. A decisão está escrita no template
   * do guia desde o [G-A6]; aqui ela só passou a ter efeito.
   */
  _registrarTurnoComErro() {}

  /**
   * O que é do TURNO morre com o turno: a pergunta seguinte limpa a anterior.
   *
   * `resetChatState` é o primeiro passo de todo `sendMessage`, então este é o
   * lugar onde "começou um turno novo" acontece uma vez só. O reset do fim da
   * CONVERSA (o "Nova conversa") mora no `limparConversa` do slice, junto do
   * recorte e da confirmação pendente, pela mesma razão de sempre: quem some
   * junto some no mesmo lugar.
   *
   * Duas coisas morrem aqui, e pela MESMA razão:
   *
   * - `erroDoTurno` ([G-A6]) — a frase do servidor é sobre a pergunta que
   *   falhou, não sobre a próxima.
   * - ★ `clientesCitados` ([G-F1], review de frontend, 24/08/2026) — o cartão
   *   de atalho nasce de quem a resposta IDENTIFICOU; sem esta linha, uma
   *   resposta que não cita ninguém deixava o cartão da anterior na tela, sob
   *   a bolha nova (AC-1 da US-1 da spec-006). ⚠️ O comportamento não estava
   *   preso em nenhum dos dois sentidos: acrescentar a limpeza deixava as 145
   *   suítes verdes, e removê-la também. O que faltava era o teste, e ele
   *   nasceu junto — dois turnos de verdade em
   *   `views/guia/view-fee-fixo-guia-junta-sse.unit.js`.
   */
  resetChatState() {
    super.resetChatState()
    store.dispatch(FEE_FIXO_IA_STORE + '/resetErroDoTurno')
    store.dispatch(FEE_FIXO_IA_STORE + '/setClientesCitados', [])
  }

  /**
   * Preserva o preview de envio INTEIRO na store.
   *
   * O captador do IA CRM guarda só `{tool, resumo, entidade, operacao}` — o
   * suficiente para o card de write-tool dele, e insuficiente aqui: o card de
   * e-mail precisa de destinatário sugerido, assunto, corpo, anexo e id da
   * análise. Sobrescrevemos aqui, e não lá, porque o formato é do Fee Fixo:
   * mexer no captador comum arriscaria o chat do CRM sem necessidade.
   *
   * @param {Object} data Payload do evento SSE `tool_result`.
   */
  _maybeCapturePendingConfirmation(data) {
    const value = (data && data.value) || {}
    const tool = (data && data.tool) || ''

    if (this._capturarLeitura(tool, value)) return

    // ★ A simulação criada pela conversa (spec-007) é escrita, mas NÃO usa o
    // card genérico: a superfície dela é o cartão que calcula e desenha os
    // componentes do Fee Lab. Por isso ela é roteada ANTES do captador comum —
    // cair no genérico montaria uma confirmação sem os números, que é pedir
    // autorização para o que o consultor não está vendo.
    if (tool === TOOL_CRIAR_SIMULACAO) {
      this._capturarSimulacao(value)
      return
    }

    // As duas escritas da persona: em cada uma, preview e commit chegam pela
    // MESMA tool. O roteamento é pelo NOME da tool, nunca pela presença de um
    // `preview_id` — os dois previews têm um, e um id sozinho não diz de qual
    // card ele veio.
    const acoesDaEscrita = ACOES_DO_ITEM_POR_TOOL[tool]
    if (acoesDaEscrita) {
      this._capturarEscrita(value, tool, acoesDaEscrita)
      return
    }

    if (value.preview_envio) {
      store.dispatch(FEE_FIXO_IA_STORE + '/setPendingConfirmation', {
        ...value,
        tool: (data && data.tool) || '',
        resolved: '',
      })
      return
    }
    super._maybeCapturePendingConfirmation(data)
  }

  /**
   * As tools de LEITURA que a tela consome — nenhuma delas pede confirmação.
   *
   * Separadas da escrita porque são coisas diferentes: aqui o resultado vira
   * conteúdo de tela; lá, um "ok" que grava. Um card de confirmação sobre uma
   * consulta pediria autorização para nada e ensinaria o consultor a confirmar
   * sem ler.
   *
   * @param {string} tool  Nome da tool do evento.
   * @param {Object} value `value` do evento SSE `tool_result`.
   * @returns {boolean} `true` quando o evento era de leitura e já foi tratado.
   */
  _capturarLeitura(tool, value) {
    // Guardamos a RECUSA do filtro também — sem ela, um filtro barrado pela
    // allow-list sumiria em silêncio e o consultor ficaria esperando uma
    // sugestão que não vem.
    if (tool === TOOL_FILTRO_CLIENTES) {
      store.dispatch(
        FEE_FIXO_IA_STORE + '/setFiltroSugerido',
        normalizarFiltroSugerido(value)
      )
      return true
    }

    // Os clientes que a resposta identificou viram o cartão de ATALHO no thread
    // (spec-006, US-1). O cartão nasce do NOME DA TOOL, nunca de markup da
    // resposta: o corpo vai com `:html="false"`, então nada que o modelo
    // escreva viraria elemento clicável. Quem decide se desenha é a view — só
    // na conversa GERAL, porque na conversa do próprio cliente não há para onde
    // levar.
    if (tool === TOOL_ESTADO_CLIENTE || tool === TOOL_PRIORIZAR_CARTEIRA) {
      // ★ [G-F4] ACUMULA, não substitui. Um turno chama a tool de estado uma
      // vez por cliente, então substituir deixava o cartão com UMA linha
      // depois de a IA nomear cinco. Quem faz o merge (e a dedup por
      // `idClienteApp`) é o slice; quem zera ENTRE turnos é o
      // `resetChatState` ([G-F1]). As duas metades são inseparáveis: acumular
      // sem zerar empilharia os citados da conversa inteira.
      store.dispatch(
        FEE_FIXO_IA_STORE + '/citarClientes',
        normalizarClientesCitados(value)
      )
      return true
    }

    // O que a tela faz com o rascunho analisado é OFERECER o ajuste — e quem
    // aplica é o clique do consultor (US-3).
    if (tool === TOOL_RASCUNHO_SIMULACAO) {
      store.dispatch(
        FEE_FIXO_IA_STORE + '/setRascunhoAnalisado',
        normalizarRascunhoAnalisado(value)
      )
      return true
    }

    return false
  }

  // O preview da simulação (o ajuste SUBSTITUI o anterior). `requer_confirmacao`
  // falso é commit, recusa ou preview sem o que confirmar: a pendência morre.
  _capturarSimulacao(value) {
    if (!value || !value.requer_confirmacao) {
      store.dispatch(FEE_FIXO_IA_STORE + '/resetSimulacaoPendente')
      return
    }
    this.registrarPreviewDaSimulacao(value)
  }

  /**
   * O preview NOVO vira a pendência da store e é depositado UMA vez — pelo
   * captador SSE (drawer e guia). O chat imersivo NÃO passa por aqui: a
   * pendência é UI dessas duas superfícies, e `ia/cartoes/cartao-simulacao.js`
   * deposita direto com os `inputs` do payload. Idempotente por `previewId`;
   * `preview_id` vazio só esconde o Confirmar; `aviso_carteira_pendente` viaja verbatim.
   */
  registrarPreviewDaSimulacao(value) {
    const previewId = value.preview_id ? String(value.preview_id) : ''
    store.dispatch(FEE_FIXO_IA_STORE + '/setSimulacaoPendente', {
      operacao: value.operacao || '',
      previewId,
      inputs: value.inputs || null,
      premissasDeclaradas: value.premissas_declaradas || null,
      resumo: value.resumo || '',
      avisoCarteiraPendente: value.aviso_carteira_pendente || '',
    })
    return this.depositarSimulacao({ previewId })
  }

  /** O ÚNICO dono do depósito de `resultados` — ver `apiFeeFixoIaDeposito.js`. */
  depositarSimulacao({ previewId, inputs } = {}) {
    return this._deposito.depositar({ previewId, inputs })
  }

  /**
   * Os dois passos de uma escrita — o que abre o card e o que o fecha. Sem o
   * ramo de fechar a pendência sobrevive à gravação (e à recusa do motor, e ao
   * preview sem o que confirmar) e o consultor vê um "Confirmar" para o que já
   * está no funil.
   *
   * @param {string[]} acoesPermitidas Ações válidas do item NESTA operação.
   */
  _capturarEscrita(value, tool, acoesPermitidas) {
    if (!value.requer_confirmacao) {
      // Commit, recusa ou preview sem o que confirmar: quem conta o desfecho é
      // a mensagem seguinte do thread, escrita a partir deste mesmo resultado.
      store.dispatch(FEE_FIXO_IA_STORE + '/resetPendingConfirmation')
      return
    }
    store.dispatch(FEE_FIXO_IA_STORE + '/setPendingConfirmation', {
      ...normalizarPreviewDeEscrita(value, acoesPermitidas),
      tool: tool,
      resolved: '',
    })
  }

  /**
   * Envia a pergunta do consultor com o contexto da tela anexado.
   *
   * São DOIS blocos, e a separação é a ADR-014-A: `contexto` diz onde ele está
   * e quem está em foco (sem dígito); `rascunho` leva os valores da tela já nos
   * nomes de parâmetro da tool, com a unidade no nome. Alargar o primeiro para
   * carregar número desfaria, por conveniência, o que a spec-003 estabeleceu.
   *
   * @param {Object}  opts
   * @param {string}  opts.message         Pergunta digitada.
   * @param {string=} opts.contexto        Bloco de `montarContexto()`.
   * @param {Object=} opts.rascunho        Payload de `montarRascunho()`, quando
   *                                       a tela do Fee Lab está aberta.
   * @param {string=} opts.conversationId  Conversa em andamento.
   * @param {string=} opts.exibir          O que aparece na BOLHA do consultor,
   *                                       quando difere do que o modelo lê. É o
   *                                       caso da confirmação de escrita: o
   *                                       protocolo da tool vai na `message`, e
   *                                       a bolha continua em português.
   * @param {Array=}  opts.anexos          Uploads JÁ concluídos do composer
   *                                       (spec-016 T023 adendo S-3) —
   *                                       `[{uploadId, nome, contentType,
   *                                       tamanho}]`. Mesmo shape do
   *                                       `view-chat.vue`; repassado direto,
   *                                       sem default — ausência não deve
   *                                       virar `null`/`[]` no corpo (mesma
   *                                       régua do `_buildChatBody`).
   * @returns {Promise<Object>}
   */
  perguntar({
    message,
    contexto,
    rascunho,
    conversationId,
    exibir,
    idClienteApp,
    anexos,
  } = {}) {
    const texto = (message || '').trim()
    if (!texto) {
      return Promise.resolve(
        this.buildErrorResponse({
          message: 'Escreva a sua pergunta antes de enviar.',
          status: 400,
        })
      )
    }
    const partes = [contexto, textoDoRascunho(rascunho), texto].filter(Boolean)
    // Modelo e thinking saem da store DESTE painel, como o `view-chat` faz com a
    // dele. Sem isto o `_buildChatBody` recebe `model: undefined`, o `if (model)`
    // não entra, e o corpo vai sem a escolha — o seletor ficaria decorativo: o
    // consultor trocaria para o Haiku e continuaria pagando o Sonnet.
    const estado = (store.state && store.state[FEE_FIXO_IA_STORE]) || {}
    return this.sendMessage({
      message: partes.join(SEPARADOR_DAS_PARTES),
      displayMessage: exibir || texto,
      conversationId: conversationId || null,
      answerStyle: 'direto',
      // De ONDE a pergunta partiu (spec-010 [T030]) — eixo ORTOGONAL a quem
      // responde: o Supervisor decide o agente, este campo só separa o custo
      // desta superfície no relatório por escritório.
      origemModulo: ORIGEM_MODULO_FEE_FIXO,
      // O cliente-alvo, quando a conversa é de um cliente (spec-005, [T008]).
      // Só é usado na CRIAÇÃO pelo backend; conversa existente não é
      // re-vinculada, então mandar sempre é inofensivo e evita um `if` aqui.
      idClienteApp: idClienteApp || null,
      model: estado.selectedModel || null,
      thinkingEnabled: estado.thinkingEnabled !== false,
      anexos: anexos,
    })
  }
}

export const apiFeeFixoIa = new APIFeeFixoIa()
export { APIFeeFixoIa }
